/**
 * API Client for Companies Admin App
 *
 * Provides methods to interact with the Accounting Hub REST API endpoints
 * for the companies admin interface.
 */

import { apiRequest, handleApiError } from '@tpct/admin-ui';
import type {
  Company,
  CompaniesListResponse,
  CompanyFormData,
  RegistryCompanySearchResponse,
} from '../types/company';
import type { UsersListResponse } from '@/users/src/types/user';

/**
 * Fetch companies list with optional filters
 */
export const fetchCompanies = async (
  filters?: {
    page?: number;
    per_page?: number;
    orderby?: string;
    order?: string;
    search?: string;
  },
  bootstrapKey: string = 'AccountingHubCompaniesAdmin'
): Promise<CompaniesListResponse> => {
  const params = new URLSearchParams();
  
  if (filters) {
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.per_page) {
      params.append('per_page', filters.per_page.toString());
    }
    if (filters.orderby) {
      params.append('orderby', filters.orderby);
    }
    if (filters.order) {
      params.append('order', filters.order);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
  }

  const queryString = params.toString();
  const endpoint = `companies${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiRequest<CompaniesListResponse>(endpoint, {}, bootstrapKey);
  
  return response;
};

/**
 * Fetch single company
 */
export const fetchCompany = async (
  companyId: number,
  bootstrapKey: string = 'AccountingHubCompaniesAdmin'
): Promise<Company> => {
  const endpoint = `companies/${companyId}`;
  const response = await apiRequest<Company>(endpoint, {}, bootstrapKey);
  return response;
};

/**
 * Create a new company
 */
export const createCompany = async (
  data: CompanyFormData,
  bootstrapKey: string = 'AccountingHubCompaniesAdmin'
): Promise<Company> => {
  const endpoint = 'companies';
  const response = await apiRequest<Company>(
    endpoint,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    bootstrapKey
  );
  return response;
};

/**
 * Update an existing company
 */
export const updateCompany = async (
  companyId: number,
  data: Partial<CompanyFormData>,
  bootstrapKey: string = 'AccountingHubCompaniesAdmin'
): Promise<Company> => {
  const endpoint = `companies/${companyId}`;
  const response = await apiRequest<Company>(
    endpoint,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
    bootstrapKey
  );
  return response;
};

/**
 * Search French public company registry (server proxy).
 */
export const searchCompanyRegistry = async (
  query: string,
  bootstrapKey: string = 'AccountingHubCompaniesAdmin',
  signal?: AbortSignal,
): Promise<RegistryCompanySearchResponse> => {
  const params = new URLSearchParams({ q: query, per_page: '10' });
  return apiRequest<RegistryCompanySearchResponse>(
    `companies/registry-search?${params.toString()}`,
    { signal },
    bootstrapKey,
  );
};

export const fetchUsersList = async (
  bootstrapKey: string = 'AccountingHubCompaniesAdmin',
): Promise<UsersListResponse> => {
  const params = new URLSearchParams({ page: '1', per_page: '100' });
  return apiRequest<UsersListResponse>(`users?${params.toString()}`, {}, bootstrapKey);
};

/**
 * Delete a company
 */
export const deleteCompany = async (
  companyId: number,
  bootstrapKey: string = 'AccountingHubCompaniesAdmin'
): Promise<void> => {
  const endpoint = `companies/${companyId}`;
  await apiRequest<void>(
    endpoint,
    {
      method: 'DELETE',
    },
    bootstrapKey
  );
};

export { handleApiError };

